/**
 * fetchUserAnalysisReport — REST call for GET /admin/analysis, the admin
 * usage-metrics dashboard (soda-5675,
 * InstantReplayCDK/specs/open/user-analysis-report.md §4).
 *
 * UserAnalysisReport/DailyTrendEntry/TopUserEntry are frontend-local
 * copies of SodaBrainFargateAgent's usage-report-store.ts types of the
 * same name -- there is no shared types package between these two repos,
 * so the two declarations must be kept in sync by hand if the backend's
 * shape changes (same convention as fetchAdminFeedback.ts's own doc
 * comment for AdminFeedbackItem).
 *
 * `avgLatencySeconds` on `DailyTrendEntry` is `number | null` -- `null` on a
 * calendar day with zero matching traffic (§4, Decisions Log #6's
 * extension). The frontend must render this as a real gap in the trend
 * line, not a `0`-valued point, or a quiet day would visually read as
 * "instant responses" instead of "no data" (see UserAnalysisReport.tsx).
 */
import { authService } from './auth';
import { API_URL } from './common/constants';
import { fetchJson } from './fetchJson';

/** Valid sinceDays window values -- must match usage-report-store.ts's (re-exported from feedback-store.ts's) VALID_SINCE_DAYS exactly. */
export const VALID_SINCE_DAYS = [7, 14, 30, 60] as const;
export type SinceDays = (typeof VALID_SINCE_DAYS)[number];

export interface DailyTrendEntry {
  date: string; // YYYY-MM-DD, UTC
  dau: number;
  conversationCount: number;
  totalQuestions: number;
  avgLatencySeconds: number | null;
  retentionFromPriorDay: number | null;
}

export interface TopUserEntry {
  userAlias: string;
  questionCount: number;
}

export interface UserAnalysisReport {
  sinceDays: SinceDays;
  summary: {
    dau: number;
    conversationCount: number;
    totalQuestions: number;
    avgQuestionsPerConversation: number;
    avgLatencySeconds: number;
  };
  dailyTrend: DailyTrendEntry[];
  topUsers: TopUserEntry[];
}

/**
 * Fetch the usage-analysis report for the last `sinceDays` days. Throws
 * if there's no valid token or the request fails (including a 400 if the
 * backend rejects `sinceDays` -- this function does not itself validate
 * the value against VALID_SINCE_DAYS, since the caller (UserAnalysisReport)
 * only ever offers the four fixed options via its own UI control, matching
 * fetchAdminFeedback.ts's identical precedent).
 */
export async function fetchUserAnalysisReport(sinceDays: SinceDays): Promise<UserAnalysisReport> {
  return fetchJson<UserAnalysisReport>(
    `${API_URL}/admin/analysis?sinceDays=${sinceDays}`,
    authService.getIdToken(),
    'usage analysis report',
  );
}
